import { csrfToken } from "./csrf"

// Upload files to a task (TaskAttachmentsController#create). Shared by the
// outline's row entry points - the ••• menu's "Attach files…", a file dropped
// on a row, an image pasted while editing it - and the Files panel itself
// (task_attachments_controller.js), so every path reports the same way:
//
//   jane:task-attachments-count  { taskId, count, html }
//     the outline redraws the row's paperclip chip; an open Files panel for
//     the task swaps in `html` (the re-rendered list) when it is present.
//
// Resolves to the server's JSON ({ ok, count, added, error, html }), or
// { ok: false, error } when nothing was sent or the request failed.
export const TASK_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024
export const TASK_ATTACHMENT_MAX_FILES = 10

export function uploadTaskAttachments(taskId, fileList, { maxBytes = TASK_ATTACHMENT_MAX_BYTES } = {}) {
    const files = Array.from(fileList || []).filter((f) => f && f.size > 0)
    const tooBig = files.filter((f) => f.size > maxBytes)
    const sendable = files.filter((f) => f.size <= maxBytes).slice(0, TASK_ATTACHMENT_MAX_FILES)
    const sizeError = tooBig.length ? `${tooBig.map((f) => f.name).join(", ")} ${tooBig.length === 1 ? "is" : "are"} over ${Math.round(maxBytes / 1024 / 1024)} MB` : null
    if (sendable.length === 0) return Promise.resolve({ ok: false, error: sizeError || "Choose a file to attach" })

    const body = new FormData()
    sendable.forEach((file, i) => {
        // A pasted screenshot arrives as "image.png" from every app; give it a
        // name that says when it was taken so two pastes don't read the same.
        const name = file.name && file.name !== "image.png" ? file.name : pastedName(file, i)
        body.append("files[]", file, name)
    })
    const headers = { Accept: "application/json", "X-CSRF-Token": csrfToken() }
    const clientId = document.querySelector("[data-task-list-client-id]")?.dataset.taskListClientId
    if (clientId) headers["X-Task-List-Client-Id"] = clientId

    return fetch(`/tasks/${encodeURIComponent(taskId)}/attachments`, { method: "POST", headers, body, credentials: "same-origin" })
        .then((r) => r.json().catch(() => ({ ok: false, error: r.status === 429 ? "Too many uploads - try again later" : "Upload failed" })))
        .catch(() => ({ ok: false, error: "Upload failed - check your connection" }))
        .then((data) => {
            if (data && typeof data.count === "number") announceTaskAttachments(taskId, data.count, data.html)
            if (sizeError) data = { ...data, error: [data?.error, sizeError].filter(Boolean).join(". ") }
            return data
        })
}

export function announceTaskAttachments(taskId, count, html = null) {
    window.dispatchEvent(new CustomEvent("jane:task-attachments-count", { detail: { taskId, count, html } }))
}

// The files a drag carries, if it carries any (an OS file drag, not a row
// being moved within the outline).
export function dragHasFiles(event) {
    return Array.from(event.dataTransfer?.types || []).includes("Files")
}

function pastedName(file, i) {
    const ext = (file.type.split("/")[1] || "png").replace("jpeg", "jpg").replace(/[^a-z0-9]/gi, "")
    const stamp = new Date().toISOString().slice(0, 19).replace("T", " ").replace(/:/g, ".")
    return `Pasted image ${stamp}${i ? ` (${i + 1})` : ""}.${ext}`
}
